import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Eye, EyeOff, Send, Upload } from "lucide-react";
import Shell from "../components/Shell";
import { useApp } from "../context/AppContext";
import { endpoints } from "../lib/api";
import { AdminGallery } from "../components/AdminGallery";
import { ProjectDetailsFields, detailsPayload, saveErrorText } from "../components/ProjectDetailsFields";

const TABS = ["Overview", "Pledges", "Offers", "Messages", "Projects", "Gallery", "Contacts", "Digests"];
const STATUSES = ["new", "reviewed", "forwarded", "in_progress", "closed", "spam"];
const TYPE_MAP = { Pledges: "pledge", Offers: "inkind", Messages: "message" };

export default function AdminPage() {
  const { auth } = useApp();
  const canEdit = ["editor", "owner"].includes(auth.role);
  const isOwner = auth.role === "owner";
  const tabs = [...TABS, ...(isOwner ? ["Admins"] : [])];
  const [tab, setTab] = React.useState("Overview");

  if (auth.loading) return <Shell><div className="flex flex-1 items-center justify-center text-on-dark-muted">Loading…</div></Shell>;
  if (!auth.authenticated) {
    return <Shell><div className="flex flex-1 flex-col items-center justify-center gap-4 p-10 text-center">
      <h1 className="font-display text-[26px] font-bold text-white">Admin sign-in</h1>
      <p className="text-on-dark-muted">Sign in with your admin email to continue.</p>
      <Link to="/signin" className="rounded-pill bg-gold-500 px-6 py-3 font-bold text-navy-900">Go to sign in</Link>
    </div></Shell>;
  }
  if (!auth.is_admin) {
    return <Shell><div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center" data-testid="admin-denied">
      <h1 className="font-display text-[26px] font-bold text-white">No access</h1>
      <p className="text-on-dark-muted">This email doesn't have admin access.</p>
    </div></Shell>;
  }

  return (
    <Shell footer={false}>
      <div className="mx-auto w-full max-w-6xl px-4 py-6">
        <h1 className="font-display text-[28px] font-extrabold text-white">Showcase admin</h1>
        <p className="mb-2 text-[13px] text-on-dark-muted">Signed in as {auth.email} · <span className="font-semibold text-gold-400">{auth.role}</span></p>
        <div className="no-scrollbar mt-2 mb-6 flex gap-2 overflow-x-auto">
          {tabs.map((t) => (
            <button key={t} data-testid={`admin-tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
              className={`whitespace-nowrap rounded-pill px-4 py-2 text-[14px] font-bold ${tab === t ? "bg-gold-500 text-navy-900" : "bg-white/5 text-white"}`}>{t}</button>
          ))}
        </div>
        {tab === "Overview" && <Overview />}
        {["Pledges", "Offers", "Messages"].includes(tab) && <Inbox type={TYPE_MAP[tab]} title={tab} canEdit={canEdit} />}
        {tab === "Projects" && <Projects canEdit={canEdit} />}
        {tab === "Gallery" && <AdminGallery canEdit={canEdit} />}
        {tab === "Contacts" && <Contacts canEdit={canEdit} />}
        {tab === "Digests" && <Digests canEdit={canEdit} />}
        {tab === "Admins" && <Admins />}
      </div>
    </Shell>
  );
}

function Card({ label, value }) {
  return (
    <div className="rounded-lg bg-white/5 p-4 ring-1 ring-white/10">
      <p className="text-[13px] text-on-dark-muted">{label}</p>
      <p className="font-display text-[28px] font-extrabold text-gold-400">{value}</p>
    </div>
  );
}

function Overview() {
  const [data, setData] = React.useState(null);
  React.useEffect(() => { endpoints.adminOverview().then(setData).catch(() => {}); }, []);
  if (!data) return <p className="text-on-dark-muted">Loading…</p>;
  return (
    <div data-testid="admin-overview">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Card label="Pledges" value={data.pledges} />
        <Card label="In-kind offers" value={data.offers} />
        <Card label="Messages" value={data.messages} />
        <Card label="Supporters" value={data.supporters} />
        <Card label="Project opens" value={data.opens} />
      </div>
      <h3 className="mt-6 mb-2 font-display text-[18px] font-bold text-white">Pledged by currency</h3>
      <div className="flex flex-wrap gap-2">
        {Object.entries(data.pledged_by_currency).length === 0 ? <p className="text-on-dark-muted">No pledges yet.</p> :
          Object.entries(data.pledged_by_currency).map(([c, v]) => (
            <span key={c} className="rounded-pill bg-white/5 px-4 py-2 font-num text-white ring-1 ring-white/10">{c} {v.toLocaleString()}</span>
          ))}
      </div>
    </div>
  );
}

function Inbox({ type, title, canEdit }) {
  const [items, setItems] = React.useState(null);
  const [status, setStatus] = React.useState("");
  const load = () => endpoints.adminSubmissions(type, status ? { status } : {}).then((d) => setItems(d.items)).catch(() => setItems([]));
  React.useEffect(() => { load(); }, [type, status]); // eslint-disable-line

  const changeStatus = async (it, s) => { await endpoints.adminUpdate(type, it.id, { status: s }); toast(`Marked ${s}`); load(); };

  return (
    <div data-testid={`admin-inbox-${type}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-white/15 bg-navy-800 px-3 py-2 text-[14px] text-white">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <a href={endpoints.adminExportUrl(type)} className="flex items-center gap-2 rounded-pill bg-white/5 px-4 py-2 text-[14px] font-semibold text-white"><Download size={15} /> Export CSV</a>
      </div>
      {items === null ? <p className="text-on-dark-muted">Loading…</p> : items.length === 0 ? (
        <p className="py-10 text-center text-on-dark-muted">Inbox zero ✨ New {title.toLowerCase()} will land here.</p>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.id} className="rounded-lg bg-white/5 p-4 ring-1 ring-white/10">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-display text-[16px] font-bold text-white">{it.project}</p>
                  <p className="text-[13px] text-on-dark-muted">{it.is_anonymous ? "Anonymous" : it.name} · {it.contact_email}{it.contact_other ? ` · ${it.contact_other}` : ""}</p>
                </div>
                <select value={it.status} disabled={!canEdit} onChange={(e) => changeStatus(it, e.target.value)} className="rounded-md border border-white/15 bg-navy-800 px-2 py-1 text-[13px] text-white disabled:opacity-50">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <p className="mt-2 text-[14px] text-white/85">
                {type === "pledge" && <span className="font-num">{it.currency} {it.amount}</span>}
                {type === "inkind" && <span>{(it.categories || []).join(", ")}{it.organisation ? ` · ${it.organisation}` : ""}</span>}
                {type === "message" && <span>{it.subject ? `${it.subject}: ` : ""}{it.body}</span>}
                {(it.note || it.details) && <span className="block text-on-dark-muted">{it.note || it.details}</span>}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Projects({ canEdit }) {
  const [data, setData] = React.useState(null);
  const [edit, setEdit] = React.useState(null);
  const load = () => endpoints.adminProjects().then((d) => setData(d.projects)).catch(() => setData([]));
  React.useEffect(() => { load(); }, []);
  const toggle = async (p) => { await endpoints.adminVisibility(p.id, !p.is_visible); load(); };
  if (!data) return <p className="text-on-dark-muted">Loading…</p>;
  return (
    <div className="overflow-x-auto" data-testid="admin-projects">
      <table className="w-full text-left text-[14px]">
        <thead className="text-on-dark-muted"><tr>
          <th className="py-2">Project</th><th>Visible</th><th>Pledges</th><th>Offers</th><th>Messages</th><th>Saves</th>{canEdit && <th></th>}
        </tr></thead>
        <tbody>
          {data.map((p) => (
            <tr key={p.id} className="border-t border-white/10">
              <td className="py-2 font-semibold text-white">{p.project}</td>
              <td><button disabled={!canEdit} onClick={() => toggle(p)} className={`${p.is_visible ? "text-mint-500" : "text-ink-400"} disabled:opacity-40`}>{p.is_visible ? <Eye size={18} /> : <EyeOff size={18} />}</button></td>
              <td className="font-num text-white">{p.counts.pledges}</td>
              <td className="font-num text-white">{p.counts.offers}</td>
              <td className="font-num text-white">{p.counts.messages}</td>
              <td className="font-num text-white">{p.counts.saves}</td>
              {canEdit && <td><button data-testid={`edit-${p.slug}`} onClick={() => setEdit(p)} className="rounded-pill bg-white/10 px-3 py-1 text-[12px] font-semibold text-white">Edit</button></td>}
            </tr>
          ))}
        </tbody>
      </table>
      {edit && <ProjectEditModal project={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

const STAGES = ["Idea", "Prototype", "MVP", "Testnet / beta", "Live"];
const TRACKS2 = ["AI", "Web3", "AI, Web3"];

function ProjectEditModal({ project, onClose, onSaved }) {
  const [full, setFull] = React.useState(null);
  const [hero, setHero] = React.useState(null);
  const [heroCard, setHeroCard] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const fileRef = React.useRef();
  const cardRef = React.useRef();

  React.useEffect(() => {
    let live = true;
    endpoints.adminGetProject(project.id).then((p) => { if (!live) return; setFull(p); setHero(p.hero_image_url); setHeroCard(p.hero_card_url); }).catch(() => {});
    return () => { live = false; };
  }, [project.id]);

  const save = async () => {
    setSaving(true);
    try {
      await endpoints.adminEditProject(project.id, {
        project: full.project, one_liner: full.one_liner, press_release: full.press_release,
        description: full.description, stage: full.stage, track: full.track, ...detailsPayload(full),
      });
      toast("Project updated");
      onSaved();
    } catch (ex) { toast(saveErrorText(ex)); } finally { setSaving(false); }
  };

  const upload = async (e, kind) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const r = await endpoints.adminUploadHero(project.id, file, kind);
      if (kind === "card") { setHeroCard(r.hero_card_url); } else { setHero(r.hero_image_url); }
      toast("Image uploaded");
    } catch { toast("Upload failed"); }
    e.target.value = "";
  };
  const clearHero = async (kind) => {
    await endpoints.adminClearHero(project.id, kind);
    if (kind === "card") setHeroCard(null); else setHero(null);
    toast("Reset to topic art");
  };

  const F = ({ label, children }) => (<label className="block"><span className="mb-1 block text-[13px] font-semibold text-ink-900">{label}</span>{children}</label>);
  const inp = "w-full rounded-md border border-line-200 bg-white px-3 py-2 text-[14px] text-ink-900 outline-none focus:border-gold-500";

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center" data-testid="project-edit-modal">
      <div className="absolute inset-0 bg-navy-950/60" onClick={onClose} />
      <div className="scroll-area relative z-10 max-h-[92vh] w-full max-w-[560px] overflow-y-auto rounded-t-sheet bg-surface-50 p-5 sm:rounded-sheet">
        <h3 className="mb-4 font-display text-[20px] font-bold text-navy-900">Edit {project.project}</h3>
        {!full ? <p className="text-ink-600">Loading…</p> : (
          <div className="space-y-3">
            <F label="Project name"><input className={inp} value={full.project} onChange={(e) => setFull({ ...full, project: e.target.value })} /></F>
            <F label="One-liner"><input className={inp} value={full.one_liner} onChange={(e) => setFull({ ...full, one_liner: e.target.value })} /></F>
            <div className="flex gap-3">
              <F label="Stage"><select className={inp} value={full.stage} onChange={(e) => setFull({ ...full, stage: e.target.value })}>{STAGES.map((s) => <option key={s}>{s}</option>)}</select></F>
              <F label="Track"><select className={inp} value={full.track} onChange={(e) => setFull({ ...full, track: e.target.value })}>{TRACKS2.map((s) => <option key={s}>{s}</option>)}</select></F>
            </div>
            <F label="Press release"><textarea rows={4} className={inp} value={full.press_release} onChange={(e) => setFull({ ...full, press_release: e.target.value })} /></F>
            <F label="Description"><textarea rows={3} className={inp} value={full.description} onChange={(e) => setFull({ ...full, description: e.target.value })} /></F>
            <ProjectDetailsFields full={full} setFull={setFull} inp={inp} prefix="admin-edit" />
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-md border border-line-200 bg-white p-3">
                <span className="mb-1 block text-[13px] font-semibold text-ink-900">Swipe hero card</span>
                <p className="mb-2 text-[11px] text-ink-400">Shows on the swipe card. Recommended 1200 × 1200 px (1:1, square). Name/branding can be baked in.</p>
                <div className="mb-2 aspect-square w-full overflow-hidden rounded-md bg-navy-100">{heroCard && <img src={heroCard} alt="" className="h-full w-full object-cover" />}</div>
                <div className="flex items-center gap-3">
                  <input ref={cardRef} type="file" accept="image/*" onChange={(e) => upload(e, "card")} className="hidden" data-testid="hero-card-file" />
                  <button onClick={() => cardRef.current.click()} data-testid="upload-hero-card" className="rounded-pill bg-navy-800 px-4 py-2 text-[13px] font-semibold text-white">Upload</button>
                  {heroCard && <button onClick={() => clearHero("card")} className="text-[13px] font-semibold text-coral-500">Reset</button>}
                </div>
              </div>
              <div className="rounded-md border border-line-200 bg-white p-3">
                <span className="mb-1 block text-[13px] font-semibold text-ink-900">Project page hero</span>
                <p className="mb-2 text-[11px] text-ink-400">Landscape, shows on the project page & grid. Recommended 1600 × 900 px (16:9), no baked-in text.</p>
                <div className="mb-2 aspect-video w-full overflow-hidden rounded-md bg-navy-100">{hero && <img src={hero} alt="" className="h-full w-full object-cover" />}</div>
                <div className="flex items-center gap-3">
                  <input ref={fileRef} type="file" accept="image/*" onChange={(e) => upload(e, "page")} className="hidden" data-testid="hero-file" />
                  <button onClick={() => fileRef.current.click()} data-testid="upload-hero-page" className="rounded-pill bg-navy-800 px-4 py-2 text-[13px] font-semibold text-white">Upload</button>
                  {hero && <button onClick={() => clearHero("page")} className="text-[13px] font-semibold text-coral-500">Reset</button>}
                </div>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={onClose} className="rounded-pill border border-line-200 bg-white px-5 py-2.5 font-semibold text-ink-900">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="project-edit-save" className="flex-1 rounded-pill bg-gold-500 py-2.5 font-bold text-navy-900 disabled:opacity-50">{saving ? "Saving…" : "Save changes"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Admins() {
  const [data, setData] = React.useState(null);
  const [email, setEmail] = React.useState("");
  const [name, setName] = React.useState("");
  const [role, setRole] = React.useState("viewer");
  const load = () => endpoints.adminAdmins().then((d) => setData(d)).catch(() => setData({ admins: [] }));
  React.useEffect(() => { load(); }, []);
  const add = async () => {
    if (!email) return;
    try { await endpoints.adminUpsertAdmin({ email, name, role }); toast("Admin saved"); setEmail(""); setName(""); load(); }
    catch { toast("Could not save admin"); }
  };
  const remove = async (e) => {
    try { await endpoints.adminRemoveAdmin(e); toast("Removed"); load(); }
    catch (err) { toast(err?.response?.data?.detail === "last_owner" ? "Can't remove the last owner" : "Could not remove"); }
  };
  if (!data) return <p className="text-on-dark-muted">Loading…</p>;
  return (
    <div data-testid="admin-admins">
      <div className="mb-5 flex flex-wrap items-end gap-2 rounded-lg bg-white/5 p-4 ring-1 ring-white/10">
        <div><label className="block text-[12px] text-on-dark-muted">Email</label><input value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-md border border-white/15 bg-navy-800 px-3 py-2 text-[14px] text-white" placeholder="person@u.center" /></div>
        <div><label className="block text-[12px] text-on-dark-muted">Name</label><input value={name} onChange={(e) => setName(e.target.value)} className="rounded-md border border-white/15 bg-navy-800 px-3 py-2 text-[14px] text-white" /></div>
        <div><label className="block text-[12px] text-on-dark-muted">Role</label><select value={role} onChange={(e) => setRole(e.target.value)} className="rounded-md border border-white/15 bg-navy-800 px-3 py-2 text-[14px] text-white"><option value="viewer">viewer</option><option value="editor">editor</option><option value="owner">owner</option><option value="builder">builder</option></select></div>
        <button onClick={add} data-testid="admin-add" className="rounded-pill bg-gold-500 px-5 py-2.5 font-bold text-navy-900">Add / update</button>
      </div>
      <div className="space-y-1">
        {data.admins.map((a) => (
          <div key={a.email} className="flex items-center justify-between rounded bg-white/5 px-4 py-2.5">
            <span className="text-white">{a.email} {a.name ? `· ${a.name}` : ""}</span>
            <div className="flex items-center gap-3">
              <span className="rounded-pill bg-gold-500/15 px-3 py-1 text-[12px] font-semibold text-gold-400">{a.role}</span>
              {a.email !== data.me.email && <button onClick={() => remove(a.email)} className="text-[13px] text-coral-500">Remove</button>}
            </div>
          </div>
        ))}
      </div>
      <p className="mt-4 text-[13px] text-on-dark-muted">Roles — <b>viewer</b>: read-only · <b>editor</b>: manage inboxes, projects, digests · <b>owner</b>: everything + manage admins · <b>builder</b>: edit only their own project(s) (linked via team contacts) + view their own inbox.</p>
    </div>
  );
}

function Contacts({ canEdit }) {
  const [data, setData] = React.useState(null);
  const [csv, setCsv] = React.useState("");
  const load = () => endpoints.adminContacts().then((d) => setData(d.contacts)).catch(() => setData([]));
  React.useEffect(() => { load(); }, []);
  const doImport = async () => {
    try { const r = await endpoints.adminContactsImport(csv); toast(`Imported ${r.imported}, skipped ${r.skipped}`); setCsv(""); load(); }
    catch { toast("Import failed"); }
  };
  return (
    <div data-testid="admin-contacts">
      {canEdit && <>
        <p className="mb-2 text-[14px] text-on-dark-muted">Team contacts are private and used only for digests. Paste CSV with header: <code className="text-gold-400">slug,name,email,is_primary</code></p>
        <textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={4} placeholder="slug,name,email,is_primary&#10;u-bot-voice,Thanadet,thanadet@example.com,true"
          className="w-full rounded-md border border-white/15 bg-navy-800 p-3 font-mono text-[13px] text-white" />
        <button onClick={doImport} className="mt-2 flex items-center gap-2 rounded-pill bg-gold-500 px-5 py-2.5 font-bold text-navy-900"><Upload size={16} /> Import contacts</button>
      </>}
      <div className="mt-6">
        {data && data.length > 0 ? (
          <div className="space-y-1">{data.map((c, i) => (
            <div key={i} className="flex justify-between rounded bg-white/5 px-3 py-2 text-[13px]"><span className="text-white">{c.project}</span><span className="text-on-dark-muted">{c.name} · {c.email}</span></div>
          ))}</div>
        ) : <p className="text-on-dark-muted">No contacts imported yet.</p>}
      </div>
    </div>
  );
}

function Digests({ canEdit }) {
  const [data, setData] = React.useState(null);
  const [sending, setSending] = React.useState(false);
  const load = () => endpoints.adminDigestPreview().then((d) => setData(d.digests)).catch(() => setData([]));
  React.useEffect(() => { load(); }, []);
  const sendable = (data || []).filter((d) => d.sendable).length;
  const send = async () => {
    setSending(true);
    try { const r = await endpoints.adminDigestSend(); toast(`Sent ${r.sent} digests ✉️`); load(); }
    catch { toast("Send failed"); } finally { setSending(false); }
  };
  if (!data) return <p className="text-on-dark-muted">Loading…</p>;
  return (
    <div data-testid="admin-digests">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-on-dark-muted">{sendable} project(s) ready (have contacts + pending support)</p>
        <button onClick={send} disabled={sending || sendable === 0 || !canEdit} className="flex items-center gap-2 rounded-pill bg-gold-500 px-5 py-2.5 font-bold text-navy-900 disabled:opacity-50"><Send size={16} /> {sending ? "Sending…" : `Send digests`}</button>
      </div>
      {data.length === 0 ? <p className="py-10 text-center text-on-dark-muted">Nothing new to send.</p> : (
        <div className="space-y-2">
          {data.map((d, i) => (
            <div key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white/5 p-4 ring-1 ring-white/10">
              <div>
                <p className="font-display text-[16px] font-bold text-white">{d.project}</p>
                <p className="text-[13px] text-on-dark-muted">{d.counts.pledge} pledges · {d.counts.inkind} offers · {d.counts.message} messages</p>
              </div>
              <span className={`rounded-pill px-3 py-1 text-[12px] font-semibold ${d.sendable ? "bg-mint-500/15 text-mint-500" : "bg-coral-500/15 text-coral-500"}`}>
                {d.sendable ? `${d.contacts.length} contact(s)` : "No contact"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
